/**
 * Business Dashboard - Post-Approval Dashboard for Business/PUDO Partners
 * Shows pending application status OR full business features if approved
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import PudoLocationCapture from '../components/PudoLocationCapture';
import { 
  Link2, ArrowLeft, Store, Package, MapPin, QrCode, CheckCircle, Clock,
  ChevronRight, Plus, DollarSign, ShoppingBag, TrendingUp, Bell, Settings,
  FileText, User, XCircle, AlertCircle, BarChart3, Users, Tag
} from 'lucide-react';
import BillingStatusWidget from '../components/BillingStatusWidget';

const BusinessDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [application, setApplication] = useState(null);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      // First check application status
      const appRes = await api.get('/applications/vendor/my-application').catch(() => null);
      if (appRes?.data?.application) setApplication(appRes.data.application);

      // If approved, fetch business data
      if (appRes?.data?.application?.status === 'approved') {
        const [businessRes, locationsRes, statsRes, ordersRes] = await Promise.all([
          api.get('/business/me').catch(() => null),
          api.get('/business/pudo/locations').catch(() => ({ data: [] })),
          api.get('/business/stats').catch(() => ({ data: {} })),
          api.get('/business/orders/recent').catch(() => ({ data: [] }))
        ]);
        if (businessRes?.data) setBusiness(businessRes.data);
        if (locationsRes?.data) setLocations(locationsRes.data);
        if (statsRes?.data) setStats(statsRes.data);
        if (ordersRes?.data) setRecentOrders(ordersRes.data);
      }
    } catch (error) {
      console.error('Failed to load business data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // APPLICATION STATUS VIEW (Pending/Rejected)
  if (!application || application.status !== 'approved') {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
              <div className="flex items-center gap-3">
                <Link2 className="h-7 w-7 text-primary" />
                <span className="text-xl font-bold">Liantiao</span>
                <Badge variant="outline" className="text-secondary border-secondary/30">BUSINESS</Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>Sign Out</Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          {!application ? (
            <Card className="text-center p-12">
              <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Application Found</h2>
              <p className="text-muted-foreground mb-6">You haven't submitted a business application yet.</p>
              <Button variant="jamaica" onClick={() => navigate('/seller/apply')}>Apply Now</Button>
            </Card>
          ) : application.status === 'pending' ? (
            <Card>
              <CardHeader className="text-center">
                <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-10 w-10 text-yellow-500" />
                </div>
                <CardTitle className="text-2xl">Application Under Review</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center text-muted-foreground">
                  <p>Thank you for applying to become a Liantiao {application.application_type === 'pudo' ? 'PUDO Centre' : 'Business Partner'}!</p>
                  <p>Our team is reviewing your application. You'll be notified once a decision is made.</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold flex items-center gap-2"><Store className="h-5 w-5" />Application Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Business Name:</span> <span className="font-medium">{application.business_name}</span></div>
                    <div><span className="text-muted-foreground">Type:</span> <span className="font-medium capitalize">{application.application_type || 'Business'}</span></div>
                    <div><span className="text-muted-foreground">Contact:</span> <span className="font-medium">{application.contact_name}</span></div>
                    <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{application.phone}</span></div>
                    <div><span className="text-muted-foreground">Parish:</span> <span className="font-medium">{application.parish}</span></div>
                    <div><span className="text-muted-foreground">Submitted:</span> <span className="font-medium">{new Date(application.created_at).toLocaleDateString()}</span></div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">Estimated review time: 2-5 business days</p>
                  <Button variant="outline" onClick={() => navigate('/my-applications')}>View All Applications</Button>
                </div>
              </CardContent>
            </Card>
          ) : application.status === 'rejected' ? (
            <Card>
              <CardHeader className="text-center">
                <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-10 w-10 text-destructive" />
                </div>
                <CardTitle className="text-2xl">Application Not Approved</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center text-muted-foreground">
                  <p>Unfortunately, your business application was not approved at this time.</p>
                  {application.rejection_reason && (
                    <div className="mt-4 p-4 bg-destructive/5 border border-destructive/20 rounded-lg text-left">
                      <p className="font-medium text-destructive mb-1">Reason:</p>
                      <p className="text-foreground">{application.rejection_reason}</p>
                    </div>
                  )}
                </div>
                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">You may reapply after addressing the feedback above.</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => navigate('/')}>Go Home</Button>
                    <Button variant="jamaica" onClick={() => navigate('/seller/apply')}>Apply Again</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </main>
      </div>
    );
  }

  // APPROVED BUSINESS DASHBOARD
  const isPUDO = application?.application_type === 'pudo' || business?.is_pudo;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold">Liantiao</span>
              <Badge className="bg-secondary text-secondary-foreground">{isPUDO ? 'PUDO' : 'SELLER'}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon"><Bell className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/business/settings')}><Settings className="h-5 w-5" /></Button>
            <Button variant="jamaica" onClick={() => navigate('/merchant/products/new')} className="gap-2">
              <Plus className="h-4 w-4" />Add Product
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Banner */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-secondary to-yellow-500 text-secondary-foreground">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Welcome, {business?.owner_name || user?.name || "Partner"}!</h1>
              <p className="text-secondary-foreground/80">
                {isPUDO ? 'Manage your PUDO centre and incoming packages' : 'Manage your products and track sales'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-secondary-foreground/80">Account Status</p>
              <Badge className="bg-green-500 text-white">Active</Badge>
            </div>
          </div>
        </div>

        {/* Billing Status Widget */}
        {!isPUDO && <BillingStatusWidget />}


        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Today's Revenue</p>
                  <p className="text-xl font-bold">J${stats?.today_revenue || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <ShoppingBag className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Orders Today</p>
                  <p className="text-xl font-bold">{stats?.today_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Tag className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Products</p>
                  <p className="text-xl font-bold">{stats?.active_products || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-xl font-bold">J${stats?.month_revenue || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Your Store Link - Share Section */}
        {!isPUDO && business?.id && (
          <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Store className="h-5 w-5 text-primary" />
                    <h3 className="font-bold text-lg">Your Store Link</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">Share your store on social media to attract more customers!</p>
                  <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
                    <span className="text-sm font-mono text-muted-foreground truncate flex-1">
                      liantiaoja.com/store/{business.id}
                    </span>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://liantiaoja.com/store/${business.id}`);
                        toast.success('Store link copied!');
                      }}
                      className="shrink-0"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                      Copy
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-green-600 border-green-600 hover:bg-green-50"
                    onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my store on Liantiao! 🛒🇯🇲\nhttps://liantiaoja.com/store/${business.id}`)}`, '_blank')}
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://liantiaoja.com/store/${business.id}`)}`, '_blank', 'width=600,height=400')}
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/store/${business.id}`)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
                    View Store
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Orders / Packages */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {isPUDO ? <Package className="h-5 w-5 text-primary" /> : <ShoppingBag className="h-5 w-5 text-primary" />}
                {isPUDO ? 'Incoming Packages' : 'Recent Orders'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(isPUDO ? '/pudo/inventory' : '/business/orders')}>
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            
            {recentOrders.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No {isPUDO ? 'packages' : 'orders'} yet</p>
                <p className="text-sm text-muted-foreground mt-1">New {isPUDO ? 'packages' : 'orders'} will appear here</p>
              </Card>
            ) : (
              recentOrders.slice(0, 5).map((order, i) => (
                <Card key={order.id || i} className="hover:border-primary/30 transition-colors cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={order.status === 'pending' ? 'secondary' : order.status === 'completed' ? 'default' : 'outline'}>
                            {order.status}
                          </Badge>
                          <span className="font-mono text-sm">{order.order_id || order.tracking_id}</span>
                        </div>
                        <p className="text-sm">{order.customer_name || 'Customer'}</p>
                        <p className="text-xs text-muted-foreground">{order.items_count || 1} item(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">J${order.total || 0}</p>
                        <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
            <div className="space-y-2">
              {isPUDO ? (
                <>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/merchant/products')}>
                    <span className="flex items-center gap-2"><Tag className="h-4 w-4" />Manage Products</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/pudo/receive')}>
                    <span className="flex items-center gap-2"><QrCode className="h-4 w-4" />Receive Package</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/pudo/release')}>
                    <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4" />Release Package</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/pudo/inventory')}>
                    <span className="flex items-center gap-2"><Package className="h-4 w-4" />View Inventory</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/merchant/products')}>
                    <span className="flex items-center gap-2"><Tag className="h-4 w-4" />Manage Products</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/merchant/fulfillment')}>
                    <span className="flex items-center gap-2"><ShoppingBag className="h-4 w-4" />View Orders</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/merchant/earnings')}>
                    <span className="flex items-center gap-2"><BarChart3 className="h-4 w-4" />Analytics</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/seller/payouts')}>
                <span className="flex items-center gap-2"><DollarSign className="h-4 w-4" />Earnings & Payouts</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/support')}>
                <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />Get Support</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* PUDO Location Info */}
            {isPUDO && locations.length > 0 && (
              <Card className="mt-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4" />Your Location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-medium">{locations[0].name}</p>
                  <p className="text-sm text-muted-foreground">{locations[0].address?.street || (typeof locations[0].address === "string" ? locations[0].address : "Address not set")}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{locations[0].hours || '8AM - 6PM'}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => navigate('/business/location/edit')}>
                    Edit Location <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BusinessDashboard;
